import { useState } from "react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Chargement, EnTetePage, Message, Vide } from "@cabinet/charte";
import { auditApi, dossiersApi, ErreurApi } from "../api/client";
import { useSession } from "../session";

const TAILLE = 100;

/** Détails qui se lisent mieux en clair qu'en identifiant. */
const CLES_MASQUEES = new Set(["dossier", "exercice", "empreinte", "pieces"]);

/**
 * Piste d'audit du tenant (EF-1901, EF-1908) : qui a fait quoi, quand, sur
 * quoi, la plus récente d'abord. Elle s'écrit en ajout seul et ne se modifie
 * pas — la base l'interdit. Une écriture mène à sa pièce.
 */
export function PageAudit() {
  const { session } = useSession();
  const [action, setAction] = useState("");
  const [dossier, setDossier] = useState("");
  const [du, setDu] = useState("");
  const [au, setAu] = useState("");

  const actions = useQuery({ queryKey: ["audit-actions"], queryFn: auditApi.actions });
  const dossiers = useQuery({ queryKey: ["dossiers"], queryFn: dossiersApi.lister });
  const evenements = useInfiniteQuery({
    queryKey: ["audit", action, dossier, du, au],
    queryFn: ({ pageParam }) =>
      auditApi.rechercher({ action: action || undefined, dossier: dossier || undefined, du: du || undefined, au: au || undefined, avant: pageParam, limite: TAILLE }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (derniere) => (derniere.length === TAILLE ? derniere[derniere.length - 1].horodatage : undefined),
  });

  const nomDuDossier = (id: string | undefined) => dossiers.data?.find((d) => d.id === id)?.raisonSociale;
  const liste = evenements.data?.pages.flat() ?? [];

  return (
    <>
      <EnTetePage
        surTitre={session?.tenant.raisonSociale}
        titre="Piste d'audit"
        description="Chaque création, modification, validation, suppression et export, avec son auteur et son heure. La piste s'écrit en ajout seul : la base refuse de la modifier ou de l'effacer."
      />

      <div className="filtres-plan">
        <select value={action} onChange={(e) => setAction(e.target.value)} aria-label="Action">
          <option value="">Toutes les actions</option>
          {(actions.data ?? []).map((a) => (
            <option key={a.code} value={a.code}>
              {a.libelle}
            </option>
          ))}
        </select>
        <select value={dossier} onChange={(e) => setDossier(e.target.value)} aria-label="Dossier">
          <option value="">Tous les dossiers</option>
          {(dossiers.data ?? []).map((d) => (
            <option key={d.id} value={d.id}>
              {d.raisonSociale}
            </option>
          ))}
        </select>
        <label className="discret-texte">
          du <input type="date" value={du} onChange={(e) => setDu(e.target.value)} />
        </label>
        <label className="discret-texte">
          au <input type="date" value={au} onChange={(e) => setAu(e.target.value)} />
        </label>
      </div>

      {evenements.error != null && (
        <Message type="erreur">{evenements.error instanceof ErreurApi ? evenements.error.message : "Lecture impossible."}</Message>
      )}
      {evenements.isPending && <Chargement quoi="la piste d'audit" />}
      {evenements.data != null && liste.length === 0 && <Vide>Aucun événement avec ces filtres.</Vide>}

      {liste.length > 0 && (
        <div className="tableau-conteneur">
          <table className="piste-d-audit">
            <thead>
              <tr>
                <th>Quand</th>
                <th>Qui</th>
                <th>Quoi</th>
                <th>Détail</th>
              </tr>
            </thead>
            <tbody>
              {liste.map((e) => {
                const dossierDe = e.detail.dossier;
                return (
                  <tr key={e.id}>
                    <td className="discret-texte">{new Date(e.horodatage).toLocaleString("fr-FR")}</td>
                    <td>
                      {e.auteur ?? "—"}
                      {e.adresseIp != null && <div className="discret-texte">{e.adresseIp}</div>}
                    </td>
                    <td>
                      {e.actionLibelle}
                      {e.entite === "Piece" && dossierDe != null && e.identifiantEntite != null && (
                        <div>
                          <Link className="discret-texte" to={`/dossiers/${dossierDe}/ecritures?piece=${e.identifiantEntite}`}>
                            Voir la pièce
                          </Link>
                        </div>
                      )}
                    </td>
                    <td className="detail-d-audit">
                      {dossierDe != null && nomDuDossier(dossierDe) != null && <span className="jeton">{nomDuDossier(dossierDe)}</span>}
                      {Object.entries(e.detail)
                        .filter(([cle]) => !CLES_MASQUEES.has(cle))
                        .map(([cle, valeur]) => (
                          <span key={cle} className="jeton">
                            <span className="discret-texte">{cle}</span> {valeur}
                          </span>
                        ))}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {evenements.hasNextPage && (
        <p>
          <button type="button" className="tertiaire" disabled={evenements.isFetchingNextPage} onClick={() => void evenements.fetchNextPage()}>
            Événements plus anciens
          </button>
        </p>
      )}
    </>
  );
}
